import { Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  HttpTransportType,
  IRetryPolicy,
  RetryContext
} from '@microsoft/signalr';
import { BehaviorSubject, Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import {
  MarketplaceNotificationRealtime,
  NOTIFICATION_EVENT_TYPE_ICONS,
  NOTIFICATION_EVENT_TYPE_LABELS
} from '../models/marketplace/marketplace-notification';

export type NotificationHubStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** Reconnect: 2s → 5s → 10s → 30s, sonra 30s aralıklarla süresiz */
const RETRY_POLICY: IRetryPolicy = {
  nextRetryDelayInMilliseconds(ctx: RetryContext): number {
    if (ctx.elapsedMilliseconds < 10_000) return 2_000;
    if (ctx.elapsedMilliseconds < 30_000) return 5_000;
    if (ctx.elapsedMilliseconds < 60_000) return 10_000;
    return 30_000;
  }
};

@Injectable({ providedIn: 'root' })
export class MarketplaceNotificationHubService implements OnDestroy {
  private readonly HUB_PATH = '/hubs/marketplace-notifications';
  private readonly EVENT_NAME = 'notificationReceived';

  private connection: HubConnection | null = null;

  // ── Public streams ────────────────────────────────────────────────────────
  private readonly _status$ = new BehaviorSubject<NotificationHubStatus>('disconnected');
  private readonly _isConnected$ = new BehaviorSubject<boolean>(false);
  private readonly _notifications$ = new Subject<MarketplaceNotificationRealtime>();
  private readonly _unreadCount$ = new BehaviorSubject<number>(0);

  readonly status$ = this._status$.asObservable();
  readonly isConnected$ = this._isConnected$.asObservable();
  readonly notifications$ = this._notifications$.asObservable();
  readonly unreadCount$ = this._unreadCount$.asObservable();

  constructor(private toastr: ToastrService) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  async connect(authToken: string, branchId: string): Promise<void> {
    const state = this.connection?.state;

    if (
      state === HubConnectionState.Connected ||
      state === HubConnectionState.Connecting ||
      state === HubConnectionState.Reconnecting
    ) {
      console.log('⏩ NotificationHub: zaten bağlı veya bağlanıyor, atlanıyor.');
      return;
    }

    // Önceki durmuş bağlantıyı temizle
    if (this.connection) {
      this.removeHandlers();
      this.connection = null;
    }

    // authToken → tenant resolution middleware
    // accessTokenFactory → ASP.NET Core JWT bearer auth (access_token query param)
    const hubUrl = `${environment.baseUrlAddress}${this.HUB_PATH}?branchId=${branchId}&authToken=${authToken}`;

    this.connection = new HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => authToken,
        transport:
          HttpTransportType.WebSockets |
          HttpTransportType.ServerSentEvents |
          HttpTransportType.LongPolling
      })
      .withAutomaticReconnect(RETRY_POLICY)
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    this.attachHandlers();
    this._status$.next('connecting');

    try {
      await this.connection.start();
      this._isConnected$.next(true);
      this._status$.next('connected');
      console.log(`✅ NotificationHub: bağlandı (branchId=${branchId})`);
    } catch (err) {
      this._isConnected$.next(false);
      this._status$.next('disconnected');
      console.error('❌ NotificationHub: bağlantı başarısız', err);
      throw err;
    }
  }

  async disconnect(): Promise<void> {
    if (!this.connection) return;

    this.removeHandlers();

    if (this.connection.state !== HubConnectionState.Disconnected) {
      try {
        await this.connection.stop();
        console.log('🛑 NotificationHub: bağlantı kapatıldı.');
      } catch (err) {
        console.warn('⚠️ NotificationHub: kapatma sırasında hata', err);
      }
    }

    this.connection = null;
    this._isConnected$.next(false);
    this._status$.next('disconnected');
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  // ── Unread counter helpers ────────────────────────────────────────────────

  setUnreadCount(count: number): void {
    this._unreadCount$.next(Math.max(0, count));
  }

  incrementUnread(): void {
    this._unreadCount$.next(this._unreadCount$.value + 1);
  }

  decrementUnread(): void {
    this._unreadCount$.next(Math.max(0, this._unreadCount$.value - 1));
  }

  resetUnread(): void {
    this._unreadCount$.next(0);
  }

  get currentUnreadCount(): number {
    return this._unreadCount$.value;
  }

  // ── Private ───────────────────────────────────────────────────────────────

  private attachHandlers(): void {
    if (!this.connection) return;

    this.connection.on(this.EVENT_NAME, (payload: MarketplaceNotificationRealtime) => {
      if (!payload?.id) return;
      this._notifications$.next(payload);
      this.incrementUnread();

      const icon = NOTIFICATION_EVENT_TYPE_ICONS[payload.eventType] ?? 'fas fa-bell';
      const label = NOTIFICATION_EVENT_TYPE_LABELS[payload.eventType] ?? '';
      const message = `
        <div class="d-flex align-items-start gap-2">
          <i class="${icon} font-size-18 text-white mt-1 flex-shrink-0"></i>
          <div>
            <div class="fw-semibold">${payload.title}</div>
            <div class="small opacity-75">${payload.body}</div>
            <div class="small opacity-50 mt-1">${label}</div>
          </div>
        </div>`;

      this.toastr.info(message, undefined, {
        enableHtml: true,
        timeOut: 6000,
        progressBar: true,
        closeButton: true
      });
    });

    this.connection.onreconnecting((err) => {
      this._isConnected$.next(false);
      this._status$.next('reconnecting');
      console.warn('🔄 NotificationHub: yeniden bağlanıyor...', err?.message ?? '');
    });

    this.connection.onreconnected((connectionId) => {
      this._isConnected$.next(true);
      this._status$.next('connected');
      console.log(`✅ NotificationHub: yeniden bağlandı (connectionId=${connectionId})`);
    });

    this.connection.onclose((err) => {
      this._isConnected$.next(false);
      this._status$.next('disconnected');
      if (err) {
        console.error('❌ NotificationHub: bağlantı hatayla kapandı', err);
      } else {
        console.log('🛑 NotificationHub: bağlantı kapandı.');
      }
    });
  }

  private removeHandlers(): void {
    this.connection?.off(this.EVENT_NAME);
  }
}
